// src/hooks/useProducts.js
// React Query hooks for product data — automatic caching + background refresh

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productApi } from '../api/productApi';

// ── Query keys (centralised so invalidation is easy) ─────────────────────
export const productKeys = {
  all:        ['products'],
  lists:      () => [...productKeys.all, 'list'],
  list:       (filters) => [...productKeys.lists(), filters],
  featured:   ['products', 'featured'],
  categories: ['products', 'categories'],
  brands:     (category) => ['products', 'brands', category || 'all'],
  detail:     (id) => ['products', id],
};

// ── List with filters (paginated) ─────────────────────────────────────────
export function useProducts(filters = {}) {
  return useQuery({
    queryKey:  productKeys.list(filters),
    queryFn:   () => productApi.getProducts(filters),
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,   // 2 minutes
  });
}

// ── Infinite scroll version ────────────────────────────────────────────────
export function useInfiniteProducts(filters = {}) {
  return useInfiniteQuery({
    queryKey: [...productKeys.list(filters), 'infinite'],
    queryFn:  ({ pageParam = 1 }) =>
      productApi.getProducts({ ...filters, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
    staleTime: 2 * 60 * 1000,
  });
}

// ── Sibling storage variants of one model (for the size switcher) ───────────
export function useVariants(variantGroup) {
  return useQuery({
    queryKey:  ['products', 'variants', variantGroup || 'none'],
    queryFn:   () => productApi.getProducts({ variantGroup, limit: 20, sort: 'low' }),
    enabled:   !!variantGroup,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Single product ─────────────────────────────────────────────────────────
export function useProduct(id) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn:  () => productApi.getProduct(id),
    enabled:  !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Featured products ──────────────────────────────────────────────────────
export function useFeaturedProducts() {
  return useQuery({
    queryKey: productKeys.featured,
    queryFn:  productApi.getFeatured,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Categories ─────────────────────────────────────────────────────────────
export function useCategories() {
  return useQuery({
    queryKey: productKeys.categories,
    queryFn:  productApi.getCategories,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Brands (optionally scoped to a category) ───────────────────────────────
export function useBrands(category) {
  return useQuery({
    queryKey: productKeys.brands(category),
    queryFn:  () => productApi.getBrands(category),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Add review mutation ────────────────────────────────────────────────────
export function useAddReview(productId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => productApi.addReview(productId, data),
    onSuccess: () => {
      qc.invalidateQueries(productKeys.detail(productId));
    },
  });
}

// ── Admin mutations ────────────────────────────────────────────────────────
export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: productApi.createProduct,
    onSuccess: () => qc.invalidateQueries(productKeys.lists()),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => productApi.updateProduct(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries(productKeys.detail(id));
      qc.invalidateQueries(productKeys.lists());
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: productApi.deleteProduct,
    onSuccess: () => qc.invalidateQueries(productKeys.lists()),
  });
}
